import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { Warehouse } from '../types/warehouse';
import { warehouseService } from '../services/warehouseService';

interface WarehouseContextType {
  currentWarehouse: string | null;
  currentWarehouseName: string | null;
  warehouses: Warehouse[];
  isLoading: boolean;
  /**
   * TW 36430604: profilen er låst til standardlageret (warehouse.lockToDefaultWarehouse).
   * Lagervalget er da fast og «Alle lager» finnes ikke. Backend håndhever låsen uansett.
   */
  isWarehouseLocked: boolean;
  setCurrentWarehouse: (warehouseId: string | null) => void;
}

const WarehouseContext = createContext<WarehouseContextType | null>(null);

interface WarehouseProviderProps {
  children: ReactNode;
}

// Synchronous initialization from localStorage (like AuthContext)
// Avoids race condition where components fetch data before warehouse is set
const initializeWarehouse = (): string | null => {
  try {
    const token = localStorage.getItem('bizclick_token');
    if (!token) return null;
    const stored = localStorage.getItem('bizclick_warehouse');
    return stored || null;
  } catch {
    return null;
  }
};

export const WarehouseProvider: React.FC<WarehouseProviderProps> = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const [currentWarehouse, setCurrentWarehouse] = useState<string | null>(() => initializeWarehouse());
  const [currentWarehouseName, setCurrentWarehouseName] = useState<string | null>(null);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isWarehouseLocked, setIsWarehouseLocked] = useState(false);

  // Keep warehouse in sync when auth state changes
  useEffect(() => {
    if (isAuthenticated && user) {
      const stored = localStorage.getItem('bizclick_warehouse');
      setCurrentWarehouse(stored || null);
    } else {
      setCurrentWarehouse(null);
      setCurrentWarehouseName(null);
      setWarehouses([]);
    }
  }, [isAuthenticated, user]);

  // TW 36430604: lagerlås per profil. Backend avgjør (setting + standardlager på profilen);
  // en låst profil får standardlageret som fast valg. Feiler oppslaget, er profilen fri i UI —
  // backend håndhever låsen uansett.
  useEffect(() => {
    if (!isAuthenticated || !user) {
      setIsWarehouseLocked(false);
      return;
    }
    let cancelled = false;
    warehouseService.getAccess()
      .then(access => {
        if (cancelled) return;
        const locked = access.isLocked && !!access.warehouseId;
        setIsWarehouseLocked(locked);
        if (locked && access.warehouseId) {
          localStorage.setItem('bizclick_warehouse', access.warehouseId);
          setCurrentWarehouse(access.warehouseId);
        }
      })
      .catch(() => { if (!cancelled) setIsWarehouseLocked(false); });
    return () => { cancelled = true; };
  }, [isAuthenticated, user]);

  const handleSetCurrentWarehouse = (warehouseId: string | null) => {
    // Låst profil: lagervalget er fast — andre valg ignoreres
    if (isWarehouseLocked) return;
    if (warehouseId) {
      localStorage.setItem('bizclick_warehouse', warehouseId);
    } else {
      localStorage.removeItem('bizclick_warehouse');
    }
    setCurrentWarehouse(warehouseId);
    const match = warehouses.find(w => w.warehouseID === warehouseId);
    setCurrentWarehouseName(match?.warehouseDescr || warehouseId);
  };

  // Fetch warehouse list when authenticated (always, regardless of currentWarehouse)
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    let cancelled = false;

    const fetchWarehouses = async () => {
      setIsLoading(true);
      try {
        const list = await warehouseService.getWarehouses();
        if (cancelled) return;

        setWarehouses(list);

        if (currentWarehouse) {
          const match = list.find(w => w.warehouseID === currentWarehouse);
          setCurrentWarehouseName(match?.warehouseDescr || currentWarehouse);
        }
      } catch (error) {
        if (cancelled) return;
        console.error('Failed to fetch warehouses:', error);
        if (currentWarehouse) {
          setCurrentWarehouseName(currentWarehouse);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchWarehouses();

    return () => { cancelled = true; };
  }, [isAuthenticated]);

  return (
    <WarehouseContext.Provider value={{ currentWarehouse, currentWarehouseName, warehouses, isLoading, isWarehouseLocked, setCurrentWarehouse: handleSetCurrentWarehouse }}>
      {children}
    </WarehouseContext.Provider>
  );
};

export const useWarehouse = () => {
  const context = useContext(WarehouseContext);
  if (!context) throw new Error('useWarehouse must be used within WarehouseProvider');
  return context;
};
